import { useCallback, useEffect, useMemo, useState } from "react";
import { Bell, CheckCircle, WarningCircle, XCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { groupNotifications, SEVERITY_TONE } from "../lib/adminNotifications";
import { toast } from "sonner";

const SEVERITY_ICON = {
  critical: XCircle,
  warning: WarningCircle,
  info: CheckCircle,
};

const BUCKETS = ["Today", "Yesterday", "Previous 7 days"];

function timeLabel(timestamp) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function AdminNotificationBell() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState({ items: [], unread_count: 0, daily_scan_summary: {} });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications?limit=40");
      setPayload(data);
    } catch {
      setPayload((prev) => ({ ...prev, items: [] }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const openPanel = () => {
      setOpen(true);
      refresh();
    };
    const refreshPanel = () => {
      refresh();
    };
    window.addEventListener("admin:open-notifications", openPanel);
    window.addEventListener("admin:notifications-refresh", refreshPanel);
    return () => {
      window.removeEventListener("admin:open-notifications", openPanel);
      window.removeEventListener("admin:notifications-refresh", refreshPanel);
    };
  }, [refresh]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const groups = useMemo(() => groupNotifications(payload.items || []), [payload.items]);
  const summaries = Object.values(payload.daily_scan_summary || {});

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/admin/notifications/mark-all-read");
      toast.success("Notifications marked read");
      await refresh();
    } catch (e) {
      toast.error("Could not mark notifications read");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative text-white/70 hover:text-white inline-flex items-center justify-center rounded border border-white/10 w-9 h-9"
        data-testid="admin-notifications-toggle"
        title="Scan notifications"
      >
        <Bell size={18} />
        {payload.unread_count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="admin-notifications-unread"
          >
            {payload.unread_count > 99 ? "99+" : payload.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[min(92vw,420px)] rounded-lg border border-white/12 bg-[var(--cc-navy-2)] shadow-xl z-50 overflow-hidden"
          data-testid="admin-notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <div className="text-sm font-semibold text-white">Scan notifications</div>
              <div className="text-[11px] text-white/45">{payload.unread_count || 0} unread</div>
            </div>
            <button
              type="button"
              onClick={markAllRead}
              disabled={busy || !payload.unread_count}
              className="text-[11px] text-[var(--cc-gold)] disabled:text-white/30"
              data-testid="admin-notifications-mark-all"
            >
              Mark all as read
            </button>
          </div>

          {summaries.length > 0 && (
            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.03]">
              <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2">Today scan summary</div>
              <div className="space-y-1.5">
                {summaries.map((s) => (
                  <div key={s.operator_name} className="text-[11px] text-white/65">
                    <span className="font-semibold text-white/85">{s.operator_name}</span>: scanned {s.scanned}, created {s.created}, updated {s.updated}, verified {s.auto_verified}, published {s.auto_published}, review {s.review_needed}, hidden {s.expired_hidden}, errors {s.errors}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            {(payload.items || []).length === 0 && (
              <div className="px-4 py-8 text-center text-white/45 text-sm">
                No new scan changes.
              </div>
            )}
            {BUCKETS.map((bucket) => (
              groups[bucket]?.length ? (
                <div key={bucket} className="border-b border-white/5 last:border-b-0">
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-white/35">
                    {bucket}
                  </div>
                  {groups[bucket].map((item) => {
                    const Icon = SEVERITY_ICON[item.severity] || CheckCircle;
                    return (
                      <a
                        key={item.notification_id}
                        href={item.deep_link || "/admin"}
                        className={`block px-4 py-3 border-l-2 ${SEVERITY_TONE[item.severity] || SEVERITY_TONE.info} bg-transparent hover:bg-white/[0.04]`}
                        data-testid={`admin-notification-${item.notification_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon size={16} className="mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-[11px] text-white/55 truncate">{item.operator_name || "System"}</div>
                              <div className="text-[10px] text-white/35 shrink-0">{timeLabel(item.timestamp)}</div>
                            </div>
                            <div className="text-xs font-semibold text-white truncate">
                              {item.competition_title || item.category}
                            </div>
                            <div className="text-[11px] text-white/65 leading-snug">
                              {item.message}
                            </div>
                            {item.action_label && (
                              <div className="text-[11px] text-[var(--cc-gold)] mt-1">
                                {item.action_label}
                              </div>
                            )}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              ) : null
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
